import {useState, useEffect} from 'react';
import {RESTStore} from '@sdk-mobile';

export type ReplaySettings = {
  application_name: string;
  beta_enabled: number;
  bit_rate: number;
  cdn_base_url: string;
  cdn_file_extension: string;
  cdn_no_replay_extension: string;
  codec: string;
  frame_rate: number;
  host: string;
  key_frame_interval: number;
  login: string;
  network_protocol: string;
  password: string;
  port: number;
  scale: number;
  share_base_url: string;
  streaming_protocol: string;
  username: string | null;
};

const cacheKey = 'replaySettingsCache';

const useReplaySettings = (cacheDuration: number) => {
  const [replaySettings, setReplaySettings] = useState<ReplaySettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const cachedData = localStorage.getItem(cacheKey);
    const cacheTime = localStorage.getItem(`${cacheKey}_time`);

    if (cachedData && cacheTime) {
      const age = Date.now() - parseInt(cacheTime, 10);
      if (age < cacheDuration) {
        setReplaySettings(JSON.parse(cachedData));
        setLoading(false);
        return;
      }
    }

    const fetchReplaySettings = async () => {
      setLoading(true);
      return new Promise<void>((resolve) => {
        RESTStore.getAnyResponse(
          'settings/replay',
          null,
          true, // failSilently
          (response: unknown, error: unknown) => {
            if (error || !response) {
              console.log('Unable to fetch replay settings:', error);
              setLoading(false);
              return;
            } else {
              console.log('Fetched replay settings:', response);
            }

            const replaySettings = response as ReplaySettings;
            setReplaySettings(replaySettings);
            localStorage.setItem(cacheKey, JSON.stringify(replaySettings));
            localStorage.setItem(`${cacheKey}_time`, Date.now().toString());
            setLoading(false);
            resolve();
          }
        );
      });
    };

    fetchReplaySettings();
  }, [cacheDuration]);

  return {replaySettings, loading};
};

export function getCachedReplaySettings() {
  const cachedData = localStorage.getItem(cacheKey);
  if (!cachedData) {
    return null;
  }
  return JSON.parse(cachedData);
}

export default useReplaySettings;
